import type { ReactNode } from "react";

import type { AgenticLogicSurface } from "@intelligems/ig-types";
import { IgAlert, IgButton, IgChip } from "@intelligems/ig-theme";
import { Box, Stack } from "@mui/material";

import { WorkspacePageFrame } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/WorkspacePageFrame";
import { WorkspaceTitle } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/WorkspaceTitle";

import { RuleDescriptionEditor } from "../rules/RuleDescriptionEditor";
import type { RuleListUsage } from "../rules/ruleListRows";
import { SURFACE_LABEL } from "../rules/surfaceLabels";
import { RuleMetaByline } from "./RuleMetaByline";
import { type RuleDetailTabKey, visibleRuleDetailTabs } from "./ruleDetailTabs";

/**
 * The surface chips beside the rule name. `null` is the degraded read — the
 * join that answers "where can this run" could not be loaded — and is kept
 * distinct from a rule that is eligible nowhere.
 */
const surfaceChipLabels = (
  eligibleSurfaces: AgenticLogicSurface[] | null
): string[] => {
  if (eligibleSurfaces === null) {
    return ["Unavailable"];
  }
  if (eligibleSurfaces.length === 0) {
    return ["None"];
  }
  return eligibleSurfaces.map((surface) => SURFACE_LABEL[surface]);
};

export interface RuleDetailPageViewProps {
  name: string;
  description: string;
  eligibleSurfaces: AgenticLogicSurface[] | null;
  usedIn: RuleListUsage[] | null;
  isRenaming: boolean;
  isSavingDescription: boolean;
  selectedTab: RuleDetailTabKey;
  onSelectedTabChange: (tab: RuleDetailTabKey) => void;
  onRename: (name: string) => Promise<void> | void;
  onSaveDescription: (description: string) => Promise<void> | void;
  onBack: () => void;
  /**
   * Copy for the back button. Comes from the surface: a standalone rule page
   * returns to the rules list, while a builder embedded in an upsell returns
   * to the settings it took over.
   */
  backLabel: string;
  /** The write actions, lifted out of the embedded workspace's title bar. */
  actions: ReactNode;
  /**
   * Whether the org may see the Advanced tab. Off withholds both the tab and
   * its body — the expert view is a per-org toggle, not a layout choice.
   */
  advancedEnabled: boolean;
  /** The embedded code (or template) workspace. */
  advanced: ReactNode;
  /** The rule-graph canvas (or its empty state) for the Selection logic tab. */
  selectionLogic: ReactNode;
  /** The rule's recommendation-settings form, on the Display settings tab. */
  displaySettings: ReactNode;
  /**
   * The rule's published-version history, on its own top-level tab. Ungated:
   * every org sees it, because what is live and how to go back are not expert
   * questions — see `ruleDetailTabs`.
   */
  versions: ReactNode;
  banners?: ReactNode;
}

/**
 * The rule detail page, as a pure view.
 *
 * Everything here is a prop so the layout can be exercised without a workspace
 * view model, a rule query, or a Monaco instance behind it — the container
 * above supplies all three.
 */
export const RuleDetailPageView = ({
  name,
  description,
  eligibleSurfaces,
  usedIn,
  isRenaming,
  isSavingDescription,
  selectedTab,
  onSelectedTabChange,
  onRename,
  onSaveDescription,
  onBack,
  backLabel,
  actions,
  advancedEnabled,
  advanced,
  selectionLogic,
  displaySettings,
  versions,
  banners,
}: RuleDetailPageViewProps) => (
  <WorkspacePageFrame
    // The rule routes mount inside `AgenticLogicPageShell`, which already
    // provides the viewport-height elevated card.
    embedded
    backLabel={backLabel}
    onBack={onBack}
    banners={
      <>
        {usedIn && usedIn.length > 1 ? (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <IgAlert
              alertKey="agentic-logic-rule-multiple-placements"
              severity="warning"
              message={`${name} is used in ${usedIn.length} experiences. Changes apply to all of them.`}
            />
          </Box>
        ) : null}
        {banners}
      </>
    }
    title={
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        spacing={4}
        sx={{ rowGap: 1 }}
      >
        <WorkspaceTitle name={name} onRename={onRename} saving={isRenaming} />
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          {surfaceChipLabels(eligibleSurfaces).map((label) => (
            <IgChip key={label} label={label} color="default" />
          ))}
        </Stack>
      </Stack>
    }
    meta={<RuleMetaByline usedIn={usedIn} />}
    summary={
      <RuleDescriptionEditor
        chrome="inline"
        description={description}
        saving={isSavingDescription}
        onSave={onSaveDescription}
      />
    }
    actions={
      <>
        <IgButton
          variant="text"
          color="secondary"
          onClick={onBack}
          sx={{ height: 40, px: 4 }}
        >
          Cancel
        </IgButton>
        {actions}
      </>
    }
    tabs={visibleRuleDetailTabs(advancedEnabled)}
    selectedTab={selectedTab}
    onSelectedTabChange={onSelectedTabChange}
  >
    {/* Every body stays mounted and the inactive ones are hidden, rather than
        switching on `selectedTab`. The Advanced tab holds an unsaved editor
        buffer and any in-flight gate run; unmounting it to glance at the
        selection logic would silently discard both. The others follow the
        same rule so one tab cannot behave differently from its neighbours —
        an expanded version card keeps its graph, and the canvas keeps its
        viewport, across a round trip to another tab. */}
    <Box
      sx={{
        display: selectedTab === "selection_logic" ? "flex" : "none",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {selectionLogic}
    </Box>
    <Box
      sx={{
        display: selectedTab === "display_settings" ? "flex" : "none",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
        overflow: "auto",
        // The canvas-slot padding from the design: 40px inline, 32px block.
        px: 10,
        py: 8,
      }}
    >
      {displaySettings}
    </Box>
    <Box
      sx={{
        display: selectedTab === "versions" ? "flex" : "none",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        px: 2,
        py: 2,
      }}
    >
      {versions}
    </Box>
    {advancedEnabled ? (
      <Box
        sx={{
          display: selectedTab === "advanced" ? "flex" : "none",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          px: 1,
        }}
      >
        {advanced}
      </Box>
    ) : null}
  </WorkspacePageFrame>
);
